import { formatDate, getDifference } from 'dates';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Typography } from 'ui';
import { Error } from 'ui/icons';

import { Tooltip } from '~/contract/components/shared';

const SignedContractStatus = ({ t }: { t: TFunction }) => (
  <Box display="flex" flexDirection="row" alignItems="center">
    <Typography component="span" variant="body2" color="textColor.light">
      {t('status.forActivation')}
    </Typography>
    <Tooltip title={t('tooltips.signed')} icon={<Error />} />
  </Box>
);

const ForValidationContractStatus = ({ t }: { t: TFunction }) => (
  <Box display="flex" flexDirection="row" alignItems="center">
    <Typography component="span" variant="body2" color="textColor.light">
      {t('status.waitingForValidation')}
    </Typography>
    <Tooltip title={t('tooltips.waitingForValidation')} icon={<Error />} />
  </Box>
);

const UnsignedContractStatus = ({ t, createdDate }: { t: TFunction; createdDate: Date }) => {
  const signingDueDate = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate() + 7);
  const remainingDays = getDifference({
    dateA: signingDueDate,
    dateB: new Date(),
    unit: 'day',
  });

  return (
    <Box display="flex" flexDirection="row" alignItems="center">
      <Typography component="span" variant="body2" color="textColor.light">
        <Trans
          i18nKey="contracts:daysToSign"
          values={{
            date: remainingDays > 0 ? remainingDays : 0,
          }}
          components={[<Typography color="orange.main" component="span" variant="inherit" display="inline" />]}
        />
      </Typography>
      <Tooltip title={t('tooltips.quote')} icon={<Error />} />
    </Box>
  );
};

const STATUSES = {
  Active: ({ startDate, t }: { startDate: Date | null; t: TFunction }) =>
    startDate ? (
      <Box display="flex" flexDirection="row" alignItems="center">
        <Typography component="span" variant="body2" color="textColor.light" display="inline">
          {t('activeFromDate', { activeFrom: formatDate({ date: startDate }) })}
        </Typography>
        <Tooltip title={t('tooltips.active')} icon={<Error />} />
      </Box>
    ) : null,
  Draft: UnsignedContractStatus,
  'Generated Contract': UnsignedContractStatus,
  'Rejected by Customer': UnsignedContractStatus,
  'Waiting for Validation': ForValidationContractStatus,
};

export const ContractStatus = ({
  status,
  startDate,
  createdDate,
}: {
  status: string;
  startDate: Date | null;
  createdDate: Date;
}) => {
  const { t } = useTranslation('contracts');

  const props = { createdDate, startDate, status, t };

  const ContractStatusText = STATUSES[status as keyof typeof STATUSES] || SignedContractStatus;

  return <ContractStatusText {...props} />;
};
